"use client"

import { useId } from "react"
import type { CSSProperties } from "react"

import { cn } from "./cn"
import styles from "./YaleAssureLock2.module.css"

export type YaleAssureFinish = "black" | "satin-nickel" | "bronze"
export type YaleAssureFeedback = "idle" | "success" | "error"
export type YaleAssureFeedbackFrame = 0 | 1 | 2 | 3 | 4 | 5 | 6 | 7
export type YaleAssureLock2Props = {
  appearance?: "material" | "clay"
  finish?: YaleAssureFinish
  isLocked?: boolean
  keypadLit?: boolean
  pressedKey?: string | null
  /** Local interaction feedback, not a reproduction of the firmware's undocumented flash sequence. */
  feedback?: YaleAssureFeedback
  /** Optional hero preview: numeric rows sweep up, then down; frame 7 settles on check. Hosts own timing. */
  feedbackFrame?: YaleAssureFeedbackFrame | null
  battery?: "normal" | "low" | "critical"
  jammed?: boolean
  disabled?: boolean
  className?: string
  onKeyPress?: (key: string) => void
}

const FINISHES = {
  black: {
    rim: ["#8b8c89", "#282a29", "#525452", "#171a19", "#767873"],
    side: "#303230",
    edge: "#959791",
  },
  "satin-nickel": {
    rim: ["#eeece6", "#888b88", "#c5c6c1", "#727671", "#e4e4dc"],
    side: "#898e8b",
    edge: "#f4f3e9",
  },
  bronze: {
    rim: ["#b38c65", "#4d4334", "#765b40", "#312e27", "#987751"],
    side: "#403d34",
    edge: "#cda477",
  },
}

const KEYS = ["1", "2", "3", "4", "5", "6", "7", "8", "9", "gear", "0", "check"]
const JAM_PATTERN = new Set(["1", "3", "5", "7", "9"])
const ACCEPTED_ROWS = [3, 2, 1, 0, 1, 2, 3, -1] as const
const position = (index: number) => ({ x: 88 + index % 3 * 72, y: 132 + Math.floor(index / 3) * 62 })

// Original Yale wordmark paths from public/img/pages/smartlocks/sqm-yale.svg.
const YALE_MARK = "M25.368 31.808C26.432 31.808 27.216 31.416 27.216 30.52V29.736H25.536C24.864 29.736 24.528 29.792 24.304 29.904C24.024 30.072 23.912 30.3519 23.912 30.688C23.912 31.3039 24.192 31.808 25.368 31.808ZM35.504 28.168C35.504 27.72 35.672 27.1039 36.008 26.768C36.344 26.432 36.792 26.264 37.352 26.264C37.912 26.264 38.36 26.432 38.696 26.768C39.032 27.1039 39.144 27.72 39.144 28.168H35.504ZM28 44.7999C37.296 44.7999 44.8 37.2959 44.8 28C44.8 18.704 37.296 11.2 28 11.2C18.704 11.2 11.2 18.704 11.2 28C11.2 37.2959 18.704 44.7999 28 44.7999ZM21.784 21.84H23.968L20.104 28.784V33.096H18.088V28.784L14.224 21.84H16.408L19.096 26.88L21.784 21.84ZM22.344 27.496C22.456 25.872 23.52 24.696 25.76 24.696C28.224 24.696 29.176 25.984 29.232 27.7759V33.152H27.384V32.312C26.88 32.928 26.152 33.264 25.032 33.264C22.792 33.264 22.064 31.92 22.064 30.744C22.064 29.792 22.456 29.176 23.128 28.728C23.688 28.336 24.416 28.224 25.536 28.224H27.272V27.72C27.272 26.88 26.936 26.32 25.76 26.32C24.752 26.32 24.36 26.768 24.304 27.496H22.344ZM40.264 25.872C39.648 25.144 38.696 24.64 37.352 24.64C35.952 24.64 35 25.144 34.384 25.872C33.656 26.768 33.488 28.056 33.488 29.008C33.488 29.904 33.656 30.968 34.272 31.864C34.888 32.704 35.896 33.264 37.464 33.264C38.528 33.264 39.368 33.04 39.984 32.592C40.6 32.144 41.048 31.416 41.104 30.688H39.144C39.088 30.968 38.92 31.1919 38.696 31.416C38.416 31.64 38.024 31.6959 37.464 31.6959C36.68 31.6959 36.176 31.472 35.896 31.024C35.672 30.688 35.56 30.128 35.56 29.624H41.216C41.216 28.112 40.992 26.768 40.264 25.872ZM33.32 31.472V33.208C30.912 33.32 30.296 32.256 30.296 30.576V21.84H32.2V30.408C32.2 31.136 32.424 31.528 33.32 31.472Z"

function KeyGlyph({ value }: { value: string }) {
  if (value === "check") {
    return (
      <path
        d="M-7 0L-1 7L9-9"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    )
  }
  if (value === "gear") {
    return (
      <g
        fill="none"
        stroke="currentColor"
        strokeWidth="2.4"
      >
        <circle r="6.2" />
        {Array.from({ length: 8 }, (_, index) => (
          <path
            key={index}
            d="M0-5.5V-8.4"
            transform={`rotate(${index * 45})`}
            strokeLinecap="square"
          />
        ))}
      </g>
    )
  }
  return (
    <text
      textAnchor="middle"
      y="8.5"
      fontFamily="Arial, Helvetica, sans-serif"
      fontSize="24"
      fontWeight="400"
      fill="currentColor"
    >
      {value}
    </text>
  )
}

/** Keyed, physical-button YRD410. Hosts own credentials, timeouts and reported lock state. */
export function YaleAssureLock2({
  appearance = "material", finish = "bronze", isLocked = true,
  keypadLit = true, pressedKey = null, feedback = "idle", feedbackFrame = null, battery = "normal",
  jammed = false, disabled = false, className, onKeyPress,
}: YaleAssureLock2Props) {
  const id = useId().replace(/:/g, "")
  const clay = appearance === "clay"
  const metal = clay
    ? { rim: ["#f8f9fa", "#c0c6cd", "#e0e4e9", "#a7b0bb", "#f2f4f6"], side: "#bac1ca", edge: "#fff" }
    : FINISHES[finish]
  const gearColor = jammed || battery === "critical" ? "#eb6249" : battery === "low" ? "#e9ae50" : undefined
  const interactive = Boolean(onKeyPress) && !disabled
  const badgePressed = pressedKey === "yale"
  const sweep = feedback === "success" && feedbackFrame !== null && keypadLit && !jammed
  const acceptedRow = sweep ? ACCEPTED_ROWS[feedbackFrame] : undefined

  function hitArea(x: number, y: number, size = 54): CSSProperties {
    return {
      left: `${(x - size / 2) / 3.2}%`, top: `${(y - size / 2) / 5.8}%`, width: `${size / 3.2}%`, height: `${size / 5.8}%`,
    }
  }

  return (
    <div
      className={cn(styles.device, className)}
      data-device="yale-assure-lock-2"
      data-appearance={appearance}
      data-finish={finish}
      data-locked={isLocked}
      data-lit={keypadLit}
      data-feedback={feedback}
      data-feedback-frame={feedbackFrame ?? undefined}
      data-sweep={sweep}
      data-jammed={jammed}
      onKeyDown={(event) => {
        // Native Enter/Space still activate the focused button. Digit shortcuts stay inside this device.
        if (interactive && !event.ctrlKey && !event.metaKey && !event.altKey && /^\d$/.test(event.key)) {
          event.preventDefault()
          onKeyPress?.(event.key)
        }
      }}
    >
      <svg
        className={styles.art}
        viewBox="0 0 320 580"
        aria-hidden="true"
        focusable="false"
      >
        <defs>
          <linearGradient
            id={`${id}-rim`}
            x1="0"
            y1="0"
            x2="1"
            y2="0.6"
          >
            {metal.rim.map((color, index) => (
              <stop
                key={index}
                offset={[0, 0.18, 0.45, 0.78, 1][index]}
                stopColor={color}
              />
            ))}
          </linearGradient>
          <linearGradient
            id={`${id}-face`}
            x1="0.1"
            y1="0"
            x2="0.8"
            y2="1"
          >
            <stop stopColor={clay ? "#eef0f3" : "#333432"} />
            <stop
              offset="0.45"
              stopColor={clay ? "#e0e4e9" : "#222320"}
            />
            <stop
              offset="1"
              stopColor={clay ? "#d0d6de" : "#171916"}
            />
          </linearGradient>
          <linearGradient
            id={`${id}-key`}
            x1="0.15"
            y1="0"
            x2="0.85"
            y2="1"
          >
            <stop stopColor={clay ? "#edf0f3" : "#30322f"} />
            <stop
              offset="1"
              stopColor={clay ? "#cfd6de" : "#222420"}
            />
          </linearGradient>
          <linearGradient
            id={`${id}-recess`}
            x1="0"
            y1="0"
            x2="0.5"
            y2="1"
          >
            <stop stopColor={clay ? "#9ea7b3" : "#050705"} />
            <stop
              offset="1"
              stopColor={clay ? "#f7f9fa" : "#454741"}
            />
          </linearGradient>
          <linearGradient
            id={`${id}-cylinder`}
            x1="0"
            y1="0"
            x2="1"
            y2="1"
          >
            <stop stopColor={clay ? "#f3f5f7" : "#41443e"} />
            <stop
              offset="0.55"
              stopColor={clay ? "#c8cfd8" : "#262a24"}
            />
            <stop
              offset="1"
              stopColor={clay ? "#e2e6eb" : "#484b44"}
            />
          </linearGradient>
          <radialGradient id={`${id}-floor`}>
            <stop
              stopColor="#3f4855"
              stopOpacity={clay ? "0.16" : "0.22"}
            />
            <stop
              offset="1"
              stopColor="#3f4855"
              stopOpacity="0"
            />
          </radialGradient>
          <filter
            id={`${id}-shadow`}
            x="-50%"
            y="-20%"
            width="200%"
            height="160%"
            colorInterpolationFilters="sRGB"
          >
            <feDropShadow
              dx="0"
              dy="13"
              stdDeviation="10"
              floodColor="#19222e"
              floodOpacity={clay ? "0.12" : "0.17"}
            />
            <feDropShadow
              dx="0"
              dy="2"
              stdDeviation="1.6"
              floodColor="#19222e"
              floodOpacity="0.16"
            />
          </filter>
          <filter
            id={`${id}-glow`}
            x="-60%"
            y="-60%"
            width="220%"
            height="220%"
          >
            <feGaussianBlur stdDeviation="1.5" />
          </filter>
          <pattern
            id={`${id}-brush`}
            width="3"
            height="4"
            patternUnits="userSpaceOnUse"
          >
            <path
              d="M0 0V4M2 1V3"
              stroke="#fff"
              strokeOpacity="0.12"
              strokeWidth="0.35"
            />
          </pattern>
        </defs>

        <ellipse
          cx="160"
          cy="548"
          rx="137"
          ry="21"
          fill={`url(#${id}-floor)`}
        />
        <g filter={`url(#${id}-shadow)`}>
          <rect
            x="29"
            y="36"
            width="262"
            height="489"
            rx="70"
            fill={metal.side}
          />
          <rect
            x="30"
            y="27"
            width="260"
            height="491"
            rx="70"
            fill={`url(#${id}-rim)`}
          />
          {!clay && (
            <rect
              x="30"
              y="27"
              width="260"
              height="491"
              rx="70"
              fill={`url(#${id}-brush)`}
            />
          )}
          <rect
            x="32"
            y="29"
            width="256"
            height="487"
            rx="68"
            fill="none"
            stroke={metal.edge}
            strokeOpacity="0.58"
            strokeWidth="0.8"
          />
          <rect
            x="36"
            y="33"
            width="248"
            height="479"
            rx="64"
            fill={clay ? "#a4aeb9" : "#080b08"}
          />
          <rect
            x="38"
            y="35"
            width="244"
            height="475"
            rx="62"
            fill={`url(#${id}-face)`}
            stroke={clay ? "#f8f9fa" : "#54564e"}
            strokeWidth="0.7"
          />
        </g>

        <g transform="translate(160 72)">
          <circle
            r="27.5"
            fill={`url(#${id}-recess)`}
          />
          <g
            className={styles.keyFace}
            data-pressed={badgePressed}
          >
            <circle
              r="25.5"
              fill={clay ? "#959fac" : "#080b08"}
            />
            <g transform="scale(1.515) translate(-28 -28)">
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d={YALE_MARK}
                fill={clay ? "#dfe4ea" : "#4a4c45"}
              />
            </g>
            <circle
              r="25.3"
              fill="none"
              stroke={clay ? "#f4f6f8" : "#73766a"}
              strokeOpacity="0.32"
              strokeWidth="0.6"
            />
          </g>
        </g>

        {KEYS.map((value, index) => {
          const { x, y } = position(index)
          const validationLit = value === "check"
            ? feedbackFrame === 7
            : /^\d$/.test(value) && Math.floor(index / 3) === acceptedRow
          const lit = value === "gear" && gearColor
            ? true
            : sweep ? validationLit : keypadLit && (!jammed || JAM_PATTERN.has(value))
          const ink = value === "gear" && gearColor ? gearColor : clay ? "#77828e" : "#fafbf4"
          return (
            <g
              key={value}
              data-key={value}
              transform={`translate(${x} ${y})`}
            >
              <circle
                r="27.5"
                fill={`url(#${id}-recess)`}
              />
              <g
                className={styles.keyFace}
                data-pressed={pressedKey === value}
              >
                <circle
                  r="25.5"
                  fill={`url(#${id}-key)`}
                  stroke={clay ? "#d7dde4" : "#080b08"}
                  strokeWidth="1.2"
                />
                <circle
                  className={styles.pressLight}
                  r="24.5"
                  fill={clay ? "#fff" : "#edf1e6"}
                  fillOpacity="0.13"
                  stroke={clay ? "#fff" : "#e3e9d8"}
                  strokeOpacity="0.3"
                  strokeWidth="0.8"
                />
                {value === "5" && [-11, 11].map((x) => (
                  <g key={x}>
                    <circle
                      cx={x}
                      cy="1"
                      r="1.8"
                      fill={clay ? "#a8b1bc" : "#10140e"}
                    />
                    <circle
                      cx={x}
                      cy="0.6"
                      r="0.8"
                      fill={clay ? "#f9fafb" : "#777e6c"}
                      fillOpacity="0.65"
                    />
                  </g>
                ))}
                <g
                  className={styles.glyph}
                  data-key={value}
                  data-warning={value === "gear" && Boolean(gearColor)}
                  style={{ color: ink, "--key-opacity": lit ? 1 : clay ? 0.42 : 0.23 } as CSSProperties}
                >
                  {!clay && (
                    <g
                      filter={`url(#${id}-glow)`}
                      opacity={lit ? 0.48 : 0}
                    >
                      <KeyGlyph value={value} />
                    </g>
                  )}
                  <KeyGlyph value={value} />
                </g>
                {value === "check" && (
                  <g
                    className={styles.accepted}
                    style={{ color: clay ? "#536274" : "#fff", opacity: feedback === "success" && (!sweep || feedbackFrame === 7) ? 1 : 0 }}
                  >
                    <KeyGlyph value={value} />
                  </g>
                )}
              </g>
            </g>
          )
        })}

        <g transform="translate(160 424)">
          <circle
            r="30"
            fill={`url(#${id}-recess)`}
          />
          <circle
            r="28.1"
            fill={`url(#${id}-cylinder)`}
            stroke={clay ? "#dfe5ea" : "#10140e"}
            strokeWidth="1.2"
          />
          <circle
            cx="0"
            cy="9"
            r="12.2"
            fill={`url(#${id}-recess)`}
          />
          <circle
            cx="0"
            cy="9"
            r="10.7"
            fill={`url(#${id}-cylinder)`}
          />
          <path
            d="M-3-12H3V0L0 4L3 8V13L0 16L2 22H-5L-4 15L-7 10L-4 5L-3 0Z"
            fill={clay ? "#7e8895" : "#070a06"}
          />
          <path
            d="M3-11V0L0 4L3 8V12"
            fill="none"
            stroke={clay ? "#f7f9fa" : "#707767"}
            strokeOpacity="0.4"
            strokeWidth="0.65"
          />
        </g>
      </svg>

      <fieldset
        className={styles.touchLayer}
        disabled={!interactive}
      >
        <legend className="sr-only">
          Yale Assure Lock 2,
          {appearance}
          .
          {jammed ? "Lock jammed" : isLocked ? "Locked" : "Unlocked"}
          .
          {keypadLit ? "Keypad awake" : "Keypad asleep"}
          .
        </legend>
        <button
          type="button"
          className={styles.key}
          style={hitArea(160, 72)}
          aria-label={isLocked ? "Yale · wake keypad" : "Yale · one-touch lock"}
          onClick={() => onKeyPress?.("yale")}
        />
        {KEYS.map((value, index) => {
          const { x, y } = position(index)
          return (
            <button
              key={value}
              type="button"
              className={styles.key}
              style={hitArea(x, y)}
              aria-label={value === "check" ? "Check · submit PIN" : value === "gear" ? "Gear · programming" : `Digit ${value}`}
              onClick={() => onKeyPress?.(value)}
            />
          )
        })}
      </fieldset>
    </div>
  )
}
